import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ExternalLink, ChevronRight, ChevronLeft } from 'lucide-react';
import { useLanguage } from './contexts/LanguageContext';
import { getTranslation } from './lib/i18n';
import { LanguageSelector } from './components/LanguageSelector';
import { CustomerPortalPanel } from './components/CustomerPortalPanel';
import Home from './Home';
import { Impressum } from './pages/Impressum';
import { NotFound } from './pages/NotFound';
import { BlogPost } from './lib/blog';
import { getPostBySlug, getAllPosts } from './lib/blog';
import { getTickerContent } from './lib/ticker';
import { getServices } from './lib/services';

function App() {
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isShrunk, setIsShrunk] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState<'normal' | 'fast'>('normal');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isPortalPanelOpen, setIsPortalPanelOpen] = useState(false);

  const [currentSlide, setCurrentSlide] = useState(1);
  const [recentPosts, setRecentPosts] = useState<BlogPost[]>([]);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isSliding, setIsSliding] = useState(false);
  const [autoSlideEnabled, setAutoSlideEnabled] = useState(true);
  const [tickerContent, setTickerContent] = useState<string[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [targetGroups, setTargetGroups] = useState<any[]>([]);
  const [shouldScrollToContact, setShouldScrollToContact] = useState(false);
  const [shouldScrollToServices, setShouldScrollToServices] = useState(false);
  const [shouldScrollToBlog, setShouldScrollToBlog] = useState(false);
  const [shouldScrollToAbout, setShouldScrollToAbout] = useState(false);
  const [isHomeReady, setIsHomeReady] = useState(false);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [isLoadingPost, setIsLoadingPost] = useState(false);

  const [translations, setTranslations] = useState<{
    nav: {
      home: string;
      services: string;
      team: string;
      blog: string;
      contact: string;
      about_us: string;
    };
    logo: {
      tagline: string;
    };
    home: {
      slides: Array<{
        title: string;
        description: string;
        image: string;
      }>;
      sections: {
        services: string;
        recentPosts: string;
        contact: {
          title: string;
          description: string;
        };
      };
      buttons: {
        details: string;
        readMore: string;
        submit: string;
      };
    };
    footer?: {
      imprint: string;
      privacy: string;
      copyright: string;
    };
  }>({
    nav: {
      home: '',
      services: '',
      team: '',
      blog: '',
      contact: '',
      about_us: ''
    },
    logo: {
      tagline: ''
    },
    home: {
      slides: [],
      sections: {
        services: '',
        recentPosts: '',
        contact: {
          title: '',
          description: ''
        }
      },
      buttons: {
        details: '',
        readMore: '',
        submit: ''
      }
    }
  });

  const [portalTranslations, setPortalTranslations] = useState<{
    title: string;
    description: string;
    buttonText: string;
    tabText: string;
    learnMore: string;
  }>({
    title: '',
    description: '',
    buttonText: '',
    tabText: '',
    learnMore: ''
  });

  const timerRef = useRef<number>();
  const scrollTimerRef = useRef<number>();
  const lastScrollY = useRef(0);
  const lastScrollTime = useRef(Date.now());
  const servicesRef = useRef<HTMLDivElement>(null);
  const scrollAttempts = useRef(0);
  const servicesScrollAttempts = useRef(0);
  const blogScrollAttempts = useRef(0);
  const aboutScrollAttempts = useRef(0);

  const isHome = location.pathname === '/';

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const scrollTo = params.get('scrollTo');
    
    if (scrollTo === 'contact') {
      setShouldScrollToContact(true);
      scrollAttempts.current = 0;
    }
  }, [location.search]);

  useEffect(() => {
    if (!isHome) {
      setIsHomeReady(false);
    }
  }, [isHome]);

  useEffect(() => {
    const loadTranslations = async () => {
      const [nav, logo, home, footer] = await Promise.all([
        getTranslation(language, 'nav'),
        getTranslation(language, 'logo'),
        getTranslation(language, 'home'),
        getTranslation(language, 'footer')
      ]);
      setTranslations({ nav, logo, home, footer });
    };
    loadTranslations();
  }, [language]);

  useEffect(() => {
    const loadPortalTranslations = async () => {
      const translations = await getTranslation(language, 'portal');
      setPortalTranslations(translations);
    };
    loadPortalTranslations();
  }, [language]);

  useEffect(() => {
    const loadTickerContent = async () => {
      const content = await getTickerContent(language);
      setTickerContent(content);
    };
    loadTickerContent();
  }, [language]);

  useEffect(() => {
    loadServices();
  }, [language]);

  const loadServices = async () => {
    const { services, targetgroups } = await getServices(language);
    setServices(services);
    setTargetGroups(targetgroups);
  };

  useEffect(() => {
    loadRecentPosts();
  }, [language]);

  const loadRecentPosts = async () => {
    const posts = await getAllPosts(language);
    setRecentPosts(posts);
  };

  useEffect(() => {
    const slug = location.pathname.split('/')[2];
    if (slug) {
      loadPost(slug);
    } else {
      setSelectedPost(null);
      setNotFound(false);
    }
  }, [location.pathname, language]);

  useEffect(() => {
    if (selectedPost) {
      window.scrollTo(0, 0);
    }
  }, [selectedPost]);

  const loadPost = async (slug: string) => {
    try {
      setIsLoadingPost(true);
      const post = await getPostBySlug(slug, language);
      if (!post) {
        setNotFound(true);
        setSelectedPost(null);
      } else {
        setSelectedPost(post);
        setNotFound(false);
      }
    } catch (error) {
      console.error('Error loading post:', error);
      setNotFound(true);
      setSelectedPost(null);
    } finally {
      setIsLoadingPost(false);
    }
  };

  const handleNavigation = (path: string, options: { 
    scrollToContact?: boolean; 
    scrollToServices?: boolean; 
    scrollToTop?: boolean;
    scrollToBlog?: boolean;
    scrollToAbout?: boolean;
  } = {}) => {
    const params = new URLSearchParams(location.search);
    const langParam = params.get('lang');
    const newPath = langParam ? `${path}?lang=${langParam}` : path;
    
    if (options.scrollToContact) {
      setShouldScrollToContact(true);
      scrollAttempts.current = 0;
    }

    if (options.scrollToServices) {
      setShouldScrollToServices(true);
      servicesScrollAttempts.current = 0;
    }

    if (options.scrollToBlog) {
      setShouldScrollToBlog(true);
      blogScrollAttempts.current = 0;
    }

    if (options.scrollToAbout) {
      setShouldScrollToAbout(true);
      aboutScrollAttempts.current = 0;
    }

    if (options.scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    navigate(newPath);
  };

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isHome) {
      handleNavigation('/', { scrollToTop: true });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    document.body.classList.remove('no-scroll');
    setIsMobileMenuOpen(false);
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isHome) {
      handleNavigation('/', { scrollToContact: true });
    } else {
      const contactSection = document.querySelector('#contact-section');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
    document.body.classList.remove('no-scroll');
    setIsMobileMenuOpen(false);
  };

  const handleServicesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isHome) {
      handleNavigation('/', { scrollToServices: true });
    } else {
      const servicesSection = document.querySelector('#services-section');
      if (servicesSection) {
        servicesSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
    document.body.classList.remove('no-scroll');
    setIsMobileMenuOpen(false);
  };

  const handleTeamClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isHome) {
      handleNavigation('/', { scrollToAbout: true });
    } else {
      const aboutSection = document.querySelector('#about-section');
      if (aboutSection) {
        aboutSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
    document.body.classList.remove('no-scroll');
    setIsMobileMenuOpen(false);
  };

  const handleBlogClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isHome) {
      handleNavigation('/', { scrollToBlog: true });
    } else {
      const blogSection = document.querySelector('#blog-section');
      if (blogSection) {
        blogSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
    document.body.classList.remove('no-scroll');
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    if (shouldScrollToContact && isHome && isHomeReady) {
      const scrollToContact = () => {
        const contactSection = document.querySelector('#contact-section');
        if (contactSection && scrollAttempts.current < 5) {
          scrollAttempts.current++;
          contactSection.scrollIntoView({ behavior: 'smooth' });
          setShouldScrollToContact(false);
        }
      };

      const timer = setTimeout(scrollToContact, 500);
      return () => clearTimeout(timer);
    }
  }, [shouldScrollToContact, isHome, isHomeReady]);

  useEffect(() => {
    if (shouldScrollToServices && isHome && isHomeReady) {
      const scrollToServices = () => {
        const servicesSection = document.querySelector('#services-section');
        if (servicesSection && servicesScrollAttempts.current < 5) {
          servicesScrollAttempts.current++;
          servicesSection.scrollIntoView({ behavior: 'smooth' });
          setShouldScrollToServices(false);
        }
      };

      const timer = setTimeout(scrollToServices, 500);
      return () => clearTimeout(timer);
    }
  }, [shouldScrollToServices, isHome, isHomeReady]);

  useEffect(() => {
    if (shouldScrollToBlog && isHome && isHomeReady) {
      const scrollToBlog = () => {
        const blogSection = document.querySelector('#blog-section');
        if (blogSection && blogScrollAttempts.current < 5) {
          blogScrollAttempts.current++;
          blogSection.scrollIntoView({ behavior: 'smooth' });
          setShouldScrollToBlog(false);
        }
      };

      const timer = setTimeout(scrollToBlog, 500);
      return () => clearTimeout(timer);
    }
  }, [shouldScrollToBlog, isHome, isHomeReady]);

  useEffect(() => {
    if (shouldScrollToAbout && isHome && isHomeReady) {
      const scrollToAbout = () => {
        const aboutSection = document.querySelector('#about-section');
        if (aboutSection && aboutScrollAttempts.current < 5) {
          aboutScrollAttempts.current++;
          aboutSection.scrollIntoView({ behavior: 'smooth' });
          setShouldScrollToAbout(false);
        }
      };

      const timer = setTimeout(scrollToAbout, 500);
      return () => clearTimeout(timer);
    }
  }, [shouldScrollToAbout, isHome, isHomeReady]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  useEffect(() => {
    if (isHome) {
      const autoSlideTimeout = setTimeout(() => {
        setAutoSlideEnabled(false);
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      }, 60000);

      return () => {
        clearTimeout(autoSlideTimeout);
      };
    }
  }, [isHome]);

  useEffect(() => {
    const handleScroll = () => {
      if (isMobileMenuOpen) return;

      const currentTime = Date.now();
      const currentScrollY = window.scrollY;
      const timeDiff = currentTime - lastScrollTime.current;
      const scrollDiff = Math.abs(currentScrollY - lastScrollY.current);
      
      const velocity = scrollDiff / timeDiff;
      
      setScrollSpeed(velocity > 0.4 ? 'fast' : 'normal');
      
      setIsScrolled(currentScrollY > 20);
      setIsShrunk(currentScrollY > 100);
      setIsScrolling(true);

      lastScrollY.current = currentScrollY;
      lastScrollTime.current = currentTime;

      if (scrollTimerRef.current) {
        window.clearTimeout(scrollTimerRef.current);
      }

      scrollTimerRef.current = window.setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimerRef.current) {
        window.clearTimeout(scrollTimerRef.current);
      }
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = (open: boolean) => {
    setIsMobileMenuOpen(open);
    if (!open) {
      setIsMobileServicesOpen(false);
    }
    document.body.classList.toggle('no-scroll', open);
  };

  const toggleAutoSlide = () => {
    setAutoSlideEnabled(prev => {
      const newState = !prev;
      if (newState) {
        startSlideTimer();
      } else if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      return newState;
    });
  };

  const startSlideTimer = useCallback(() => {
    if (!autoSlideEnabled) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = window.setInterval(() => {
      goToNext();
    }, 5000);
  }, [autoSlideEnabled]);

  useEffect(() => {
    if (isHome && autoSlideEnabled && !isSliding) {
      startSlideTimer();
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [startSlideTimer, isHome, autoSlideEnabled, isSliding]);

  const handleTransitionEnd = () => {
    setIsSliding(false);
    if (currentSlide === 0) {
      setCurrentSlide(translations.home.slides.length);
    } else if (currentSlide === translations.home.slides.length + 1) {
      setCurrentSlide(1);
    }
  };

  const goToSlide = (index: number) => {
    if (isSliding) return;
    setIsSliding(true);
    setCurrentSlide(index + 1);
    setAutoSlideEnabled(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const goToPrevious = () => {
    if (isSliding) return;
    setIsSliding(true);
    setCurrentSlide(prev => prev - 1);
    setAutoSlideEnabled(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const goToNext = () => {
    if (isSliding) return;
    setIsSliding(true);
    setCurrentSlide(prev => prev + 1);
    if (!autoSlideEnabled && timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const headerHeight = isShrunk ? 48 : 80;
  const tickerHeight = 40;
  const actualTickerHeight = isHome ? tickerHeight : 0;

  if (!translations.nav || !translations.logo || !translations.home) {
    return null;
  }

  if (notFound) {
    return <NotFound />;
  }

  if (isLoadingPost) {
    return (
      <div className="min-h-screen bg-white pt-32">
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mb-8"></div>
            <div className="space-y-4 mb-8">
              <div className="h-4 bg-gray-200 rounded w-24"></div>
              <div className="h-4 bg-gray-200 rounded w-32"></div>
            </div>
            <div className="space-y-6">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              <div className="h-4 bg-gray-200 rounded w-4/5"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (selectedPost) {
    return (
      <div className={`min-h-screen bg-white ${isScrolling ? `is-scrolling scroll-speed-${scrollSpeed}` : ''}`}>
        <nav 
          className="fixed w-full z-50"
          style={{ top: 0 }}
        >
          <div 
            className="bg-bamboo-600/90 backdrop-blur-sm border-b border-bamboo-700/20"
            style={{ height: `${headerHeight}px` }}
          >
            <div className="container mx-auto h-full">
              <div className="flex justify-between items-center h-full px-4">
                <div className="flex flex-col py-2">
                  <a 
                    href="/"
                    onClick={(e) => { e.preventDefault(); handleNavigation('/'); }}
                    className="group"
                  >
                    <div className={`font-bold text-white transform-gpu group-hover:text-bamboo-100 ${isShrunk ? 'text-xl' : 'text-4xl tracking-[0.3em] mb-0.5'}`} translate="no">楽ドイツ</div>
                    <div className={`text-bamboo-100 text-sm group-hover:text-white ${isShrunk ? 'opacity-0 h-0' : 'opacity-100 h-6'}`}>
                      {translations.logo.tagline}
                    </div>
                  </a>
                </div>

                <div className="hidden lg:flex items-center">
                  <div className="flex items-center space-x-8">
                    <a 
                      href="/"
                      onClick={handleHomeClick}
                      className="text-bamboo-100 hover:text-white transition-colors whitespace-nowrap"
                    >
                      {translations.nav.home}
                    </a>
                    <a 
                      href="#services-section"
                      onClick={handleServicesClick}
                      className="text-bamboo-100 hover:text-white transition-colors whitespace-nowrap"
                    >
                      {translations.nav.services}
                    </a>
                    <a 
                      href="#about-section"
                      onClick={handleTeamClick}
                      className="text-bamboo-100 hover:text-white transition-colors whitespace-nowrap"
                    >
                      {translations.nav.about_us}
                    </a>
                    <a 
                      href="#blog-section"
                      onClick={handleBlogClick}
                      className="text-bamboo-100 hover:text-white transition-colors whitespace-nowrap"
                    >
                      {translations.nav.blog}
                    </a>
                    <a 
                      href="#contact-section"
                      onClick={handleContactClick}
                      className="text-bamboo-100 hover:text-white transition-colors whitespace-nowrap"
                    >
                      {translations.nav.contact}
                    </a>
                  </div>
                  <div className="flex items-center ml-8 space-x-4 border-l border-bamboo-500/30 pl-8">
                    <LanguageSelector
                      currentLanguage={language}
                      onLanguageChange={setLanguage}
                    />
                  </div>
                </div>

                <button 
                  onClick={() => toggleMobileMenu(!isMobileMenuOpen)}
                  className="lg:hidden text-white p-2"
                >
                  {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
            </div>
          </div>

          <div 
            className={`fixed top-0 right-0 h-full w-64 bg-bamboo-600/90 backdrop-blur-sm mobile-menu z-[60] ${
              isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
            } lg:hidden`}
            style={{ marginTop: headerHeight }}
          >
            <div className="flex flex-col p-4">
              <a 
                href="/"
                onClick={handleHomeClick}
                className="text-bamboo-100 hover:text-white py-3 border-b border-bamboo-500/30 transition-colors"
              >
                {translations.nav?.home}
              </a>
              <a 
                href="#services-section"
                onClick={handleServicesClick}
                className="text-bamboo-100 hover:text-white py-3 border-b border-bamboo-500/30 transition-colors"
              >
                {translations.nav?.services}
              </a>
              <a 
                href="#about-section"
                onClick={handleTeamClick}
                className="text-bamboo-100 hover:text-white py-3 border-b border-bamboo-500/30 transition-colors"
              >
                {translations.nav?.about_us}
              </a>
              <a 
                href="#blog-section"
                onClick={handleBlogClick}
                className="text-bamboo-100 hover:text-white py-3 border-b border-bamboo-500/30 transition-colors"
              >
                {translations.nav?.blog}
              </a>
              <a 
                href="#contact-section"
                onClick={handleContactClick}
                className="text-bamboo-100 hover:text-white py-3 border-b border-bamboo-500/30 transition-colors"
              >
                {translations.nav?.contact}
              </a>

              <div className="mt-6 p-4 bg-white/10 rounded-lg">
                <h3 className="text-white font-semibold mb-2">{portalTranslations.title}</h3>
                <p className="text-bamboo-100 text-sm mb-4">{portalTranslations.description}</p>
                <button 
                  onClick={() => {
                    const blogSlug = language === 'de' ? 'kundenportal' : 'client-portal';
                    const params = new URLSearchParams(location.search);
                    const langParam = params.get('lang');
                    const blogPath = `/blog/${blogSlug}${langParam ? `?lang=${langParam}` : ''}`;
                    navigate(blogPath);
                    toggleMobileMenu(false);
                  }}
                  className="text-bamboo-100 hover:text-white flex items-center gap-1 text-sm group mb-4"
                >
                  {portalTranslations.learnMore}
                  <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
                <a
                  href="https://rakudoitsu-client-portal.powerappsportals.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-bamboo-800 px-4 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-bamboo-50 transition-colors w-full text-sm"
                  onClick={() => toggleMobileMenu(false)}
                >
                  {portalTranslations.buttonText}
                  <ExternalLink size={16} />
                </a>
              </div>

              <div className="flex space-x-4 mt-4">
                <LanguageSelector
                  currentLanguage={language}
                  onLanguageChange={(newLang) => {
                    setLanguage(newLang);
                    document.body.classList.remove('no-scroll');
                    toggleMobileMenu(false);
                  }}
                />
              </div>
            </div>
          </div>
        </nav>

        <div 
          className={`fixed inset-0 bg-black transition-opacity duration-300 lg:hidden ${
            isMobileMenuOpen ? 'opacity-50 z-40' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => toggleMobileMenu(false)}
        ></div>

        <div className="min-h-screen bg-white pt-32">
          <div className="container mx-auto px-4">
            <button
              onClick={() => handleNavigation('/', { scrollToBlog: true })}
              className="text-bamboo-600 hover:text-bamboo-700 mb-8 flex items-center gap-2"
            >
              <ChevronLeft size={20} /> {language === 'de' ? 'Zurück zur Übersicht' : 'ブログ一覧に戻る'}
            </button>
            <article className="prose prose-lg max-w-none">
              <div className="mb-8">
                <time className="text-sm text-gray-500">{formatDate(selectedPost.date)}</time>
              </div>
              <div dangerouslySetInnerHTML={{ __html: selectedPost.content || '' }}></div>
            </article>
            <div className="mt-12 mb-24">
              <button
                onClick={() => handleNavigation('/', { scrollToBlog: true })}
                className="text-bamboo-600 hover:text-bamboo-700 flex items-center gap-2"
              >
                <ChevronLeft size={20} /> {language === 'de' ? 'Zurück zur Übersicht' : 'ブログ一覧に戻る'}
              </button>
            </div>
          </div>
        </div>

        <footer className="bg-bamboo-50 py-12">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-2xl font-bold text-bamboo-800 mb-6 md:mb-0" translate="no">楽ドイツ</div>
              <div className="flex flex-col md:flex-row gap-4 md:gap-8 items-center">
                <a 
                  href="/impressum" 
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/impressum');
                  }}
                  className="text-bamboo-700 hover:text-bamboo-800"
                >
                  {translations.footer?.imprint}
                </a>
                <a href="/content/rakudoitsu-privacy-policy-31012024.pdf" target="_blank" className="text-bamboo-700 hover:text-bamboo-800">
                  {translations.footer?.privacy}
                </a>
                <span className="text-bamboo-700">
                  {translations.footer?.copyright}
                </span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-white ${isScrolling ? `is-scrolling scroll-speed-${scrollSpeed}` : ''}`}>
      {isHome && (
        <CustomerPortalPanel 
          isOpen={isPortalPanelOpen} 
          onClose={() => setIsPortalPanelOpen(!isPortalPanelOpen)} 
        />
      )}
      
      {isHome && (
        <div 
          className="fixed w-full top-0 bg-bamboo-100 text-bamboo-800 overflow-hidden z-50"
          style={{
            height: isScrolled ? 0 : tickerHeight,
            transform: `translateY(${isScrolled ? '-100%' : '0'})`,
          }}
        >
          <div className="ticker-container relative h-full flex items-center">
            <div className="flex whitespace-nowrap animate-ticker">
              {[...tickerContent, ...tickerContent].map((city, index) => (
                <span 
                  key={index} 
                  className="inline-block px-12 py-2"
                >
                  {city}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <nav 
        className="fixed w-full z-50"
        style={{
          top: isHome && !isScrolled ? `${tickerHeight}px` : 0,
        }}
      >
        <div 
          className="bg-bamboo-600/90 backdrop-blur-sm border-b border-bamboo-700/20"
          style={{ height: `${headerHeight}px` }}
        >
          <div className="container mx-auto h-full">
            <div className="flex justify-between items-center h-full px-4">
              <div className="flex flex-col py-2">
                <a 
                  href="/"
                  onClick={(e) => { e.preventDefault(); handleNavigation('/'); }}
                  className="group"
                >
                  <div className={`font-bold text-white transform-gpu group-hover:text-bamboo-100 ${isShrunk ? 'text-xl' : 'text-4xl tracking-[0.3em] mb-0.5'}`} translate="no">楽ドイツ</div>
                  <div className={`text-bamboo-100 text-sm group-hover:text-white ${isShrunk ? 'opacity-0 h-0' : 'opacity-100 h-6'}`}>
                    {translations.logo.tagline}
                  </div>
                </a>
              </div>

              <div className="hidden lg:flex items-center">
                <div className="flex items-center space-x-8">
                  <a 
                    href="/"
                    onClick={handleHomeClick}
                    className="text-bamboo-100 hover:text-white transition-colors whitespace-nowrap"
                  >
                    {translations.nav.home}
                  </a>
                  <a 
                    href="#services-section"
                    onClick={handleServicesClick}
                    className="text-bamboo-100 hover:text-white transition-colors whitespace-nowrap"
                  >
                    {translations.nav.services}
                  </a>
                  <a 
                    href="#about-section"
                    onClick={handleTeamClick}
                    className="text-bamboo-100 hover:text-white transition-colors whitespace-nowrap"
                  >
                    {translations.nav.about_us}
                  </a>
                  <a 
                    href="#blog-section"
                    onClick={handleBlogClick}
                    className="text-bamboo-100 hover:text-white transition-colors whitespace-nowrap"
                  >
                    {translations.nav.blog}
                  </a>
                  <a 
                    href="#contact-section"
                    onClick={handleContactClick}
                    className="text-bamboo-100 hover:text-white transition-colors whitespace-nowrap"
                  >
                    {translations.nav.contact}
                  </a>
                </div>
                <div className="flex items-center ml-8 space-x-4 border-l border-bamboo-500/30 pl-8">
                  <LanguageSelector
                    currentLanguage={language}
                    onLanguageChange={setLanguage}
                  />
                </div>
              </div>

              <button 
                onClick={() => toggleMobileMenu(!isMobileMenuOpen)}
                className="lg:hidden text-white p-2"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        <div 
          className={`fixed top-0 right-0 h-full w-64 bg-bamboo-600/90 backdrop-blur-sm mobile-menu z-[60] ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          } lg:hidden`}
          style={{ marginTop: headerHeight }}
        >
          <div className="flex flex-col p-4">
            <a 
              href="/"
              onClick={handleHomeClick}
              className="text-bamboo-100 hover:text-white py-3 border-b border-bamboo-500/30 transition-colors"
            >
              {translations.nav?.home}
            </a>
            <a 
              href="#services-section"
              onClick={handleServicesClick}
              className="text-bamboo-100 hover:text-white py-3 border-b border-bamboo-500/30 transition-colors"
            >
              {translations.nav?.services}
            </a>
            <a 
              href="#about-section"
              onClick={handleTeamClick}
              className="text-bamboo-100 hover:text-white py-3 border-b border-bamboo-500/30 transition-colors"
            >
              {translations.nav?.about_us}
            </a>
            <a 
              href="#blog-section"
              onClick={handleBlogClick}
              className="text-bamboo-100 hover:text-white py-3 border-b border-bamboo-500/30 transition-colors"
            >
              {translations.nav?.blog}
            </a>
            <a 
              href="#contact-section"
              onClick={handleContactClick}
              className="text-bamboo-100 hover:text-white py-3 border-b border-bamboo-500/30 transition-colors"
            >
              {translations.nav?.contact}
            </a>

            <div className="mt-6 p-4 bg-white/10 rounded-lg">
              <h3 className="text-white font-semibold mb-2">{portalTranslations.title}</h3>
              <p className="text-bamboo-100 text-sm mb-4">{portalTranslations.description}</p>
              <button 
                onClick={() => {
                  const blogSlug = language === 'de' ? 'kundenportal' : 'client-portal';
                  const params = new URLSearchParams(location.search);
                  const langParam = params.get('lang');
                  const blogPath = `/blog/${blogSlug}${langParam ? `?lang=${langParam}` : ''}`;
                  navigate(blogPath);
                  toggleMobileMenu(false);
                }}
                className="text-bamboo-100 hover:text-white flex items-center gap-1 text-sm group mb-4"
              >
                {portalTranslations.learnMore}
                <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href="https://rakudoitsu-client-portal.powerappsportals.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-bamboo-800 px-4 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-bamboo-50 transition-colors w-full text-sm"
                onClick={() => toggleMobileMenu(false)}
              >
                {portalTranslations.buttonText}
                <ExternalLink size={16} />
              </a>
            </div>

            <div className="flex space-x-4 mt-4">
              <LanguageSelector
                currentLanguage={language}
                onLanguageChange={(newLang) => {
                  setLanguage(newLang);
                  document.body.classList.remove('no-scroll');
                  toggleMobileMenu(false);
                }}
              />
            </div>
          </div>
        </div>
      </nav>

      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 lg:hidden ${
          isMobileMenuOpen ? 'opacity-50 z-40' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => toggleMobileMenu(false)}
      ></div>

      <Routes>
        <Route path="/" element={
          <Home 
            slides={translations.home.slides}
            services={services}
            targetGroups={targetGroups}
            servicesTitle={translations.home.sections.services}
            recentPostsTitle={translations.home.sections.recentPosts}
            contactTitle={translations.home.sections.contact.title}
            contactDescription={translations.home.sections.contact.description}
            buttonLabels={{
              details: translations.home.buttons.details,
              readMore: translations.home.buttons.readMore,
              submit: translations.home.buttons.submit
            }}
            recentPosts={recentPosts}
            currentSlide={currentSlide}
            goToSlide={goToSlide}
            goToPrevious={goToPrevious}
            goToNext={goToNext}
            formatDate={formatDate}
            actualTickerHeight={actualTickerHeight}
            isSliding={isSliding}
            onTransitionEnd={handleTransitionEnd}
            autoSlideEnabled={autoSlideEnabled}
            toggleAutoSlide={toggleAutoSlide}
            language={language}
            onReady={() => setIsHomeReady(true)}
          />
        } />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      <footer className="bg-bamboo-50 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-2xl font-bold text-bamboo-800 mb-6 md:mb-0" translate="no">楽ドイツ</div>
            <div className="flex flex-col md:flex-row gap-4 md:gap-8 items-center">
              <a 
                href="/impressum" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/impressum');
                }}
                className="text-bamboo-700 hover:text-bamboo-800"
              >
                {translations.footer?.imprint}
              </a>
              <a href="/content/rakudoitsu-privacy-policy-31012024.pdf" target="_blank" className="text-bamboo-700 hover:text-bamboo-800">
                {translations.footer?.privacy}
              </a>
              <span className="text-bamboo-700">
                {translations.footer?.copyright}
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;